import { useEffect, useMemo, useState } from "react";
import api, { API, fmt, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Receipt, HandCoins, Trash2, Pencil, Sparkles, Loader2, X, AlertTriangle, ShieldCheck, Search, ExternalLink, Files, Printer, CheckSquare, Square, RotateCw, Clock, AlertOctagon, FileText, Check } from "lucide-react";

const KINDS = [
    { v: "", label: "All" },
    { v: "vendor", label: "Vendor bills" },
    { v: "cash_imprest", label: "Cash / Imprest" },
];

const emptyLine = { name: "", hsn: "", qty: 0, unit: "Pack", rate: 0, tax_percent: 0, tax_amount: null, line_total: 0 };

export default function PurchasesPage() {
    const [rows, setRows] = useState([]);
    const [kind, setKind] = useState("");
    const [q, setQ] = useState("");
    const [draft, setDraft] = useState(null);              // vendor-bill draft (extracted)
    const [cashOpen, setCashOpen] = useState(false);
    const [cashDraft, setCashDraft] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Bulk upload state — array of drafts, each can be reviewed/saved individually
    const [bulkDrafts, setBulkDrafts] = useState(null); // null when closed
    const [bulkProgress, setBulkProgress] = useState(null); // { total, done } during upload
    // Multi-select for bulk print
    const [selected, setSelected] = useState(new Set());
    const [printing, setPrinting] = useState(false);

    const toggleSel = (id) => setSelected((s) => {
        const n = new Set(s);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });
    const toggleSelAll = () => setSelected((s) => {
        if (s.size === rows.length) return new Set();
        return new Set(rows.filter((r) => r.has_attachment).map((r) => r.id));
    });

    const load = () => api.get("/purchases", { params: { kind, q } }).then((r) => setRows(r.data));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, q]);

    // --- Duplicate-bill banner state ---
    const [dupCount, setDupCount] = useState(0);
    const [dupModal, setDupModal] = useState(false);
    const [dupDismissed, setDupDismissed] = useState(false);

    const refreshDupCount = async () => {
        try {
            const r = await api.get("/purchases/duplicates");
            setDupCount(r.data.count || 0);
        } catch { /* silent */ }
    };
    // Refresh count on mount + whenever rows change (post-delete)
    // eslint-disable-next-line
    useEffect(() => { refreshDupCount(); }, [rows.length]);

    const totals = useMemo(() => rows.reduce((a, r) => {
        a.grand += r.grand_total || 0; a.tax += r.total_tax || 0; return a;
    }, { grand: 0, tax: 0 }), [rows]);

    // --- Vendor bill upload flow ---
    const uploadVendorBill = async (file) => {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await api.post("/purchases/extract", fd, { timeout: 120000 });
            const d = r.data;
            // Normalize into an editable draft
            setDraft({
                kind: "vendor",
                vendor: d.vendor || {},
                bill_number: d.bill_number || "",
                bill_date: d.bill_date || "",
                line_items: (d.line_items || []).map((l) => ({ ...emptyLine, ...stripNulls(l) })),
                subtotal: d.subtotal || 0, cgst: d.cgst || 0, sgst: d.sgst || 0, igst: d.igst || 0,
                total_tax: d.total_tax || 0, grand_total: d.grand_total || 0,
                purchased_by: "", purpose: "",
                file_name: d.file_name, file_mime: d.file_mime, file_data: d.file_data,
                confidence: d.confidence || {},
                notes: "",
                duplicate_of: d.duplicate_of,
            });
            toast.success("Bill parsed — review the fields below then save");
        } catch (e) { toast.error(e.response?.data?.detail || "Extraction failed"); }
        setUploading(false);
    };

    const saveDraft = async () => {
        if (!draft) return;
        try {
            await api.post("/purchases", draft);
            toast.success("Purchase saved");
            setDraft(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    // --- Cash / imprest ---
    const startCash = () => {
        setCashOpen(true);
        setCashDraft({
            kind: "cash_imprest",
            purchased_by: "", purpose: "",
            vendor: { name: "" },
            bill_number: "", bill_date: new Date().toISOString().slice(0, 10),
            line_items: [], grand_total: 0, total_tax: 0, subtotal: 0,
            file_name: "", file_mime: "", file_data: "", notes: "",
        });
    };
    const attachCashFile = async (file) => {
        if (!file) return;
        const b64 = await new Promise((res) => {
            const fr = new FileReader();
            fr.onload = () => res(fr.result.split(",")[1]);
            fr.readAsDataURL(file);
        });
        setCashDraft({ ...cashDraft, file_name: file.name, file_mime: file.type, file_data: b64 });
    };
    const saveCash = async () => {
        if (!cashDraft.purchased_by.trim()) return toast.error("Staff name required");
        if (!(cashDraft.grand_total > 0)) return toast.error("Amount required");
        try {
            await api.post("/purchases", cashDraft);
            toast.success(`Cash purchase logged for ${cashDraft.purchased_by}`);
            setCashOpen(false); setCashDraft(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete purchase ${p.bill_number || p.id}?`)) return;
        try { await api.delete(`/purchases/${p.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    // --- Bulk upload (multi-file → per-bill review list)
    // Strategy: fire one HTTP request PER FILE against the existing /purchases/extract
    // endpoint, with a concurrency pool of 3. Each file completes independently so
    // one slow/failed file cannot poison the batch, and the user sees live progress
    // as each row transitions extracting → ready (or failed → retryable).
    const BULK_CONCURRENCY = 3;

    // Strip null/undefined values so JS spread doesn't overwrite defaults with null
    // (the AI extractor happily returns hsn:null, unit:null, etc., which then breaks
    // the Pydantic-validated POST /purchases save).
    const stripNulls = (o) => Object.fromEntries(
        Object.entries(o || {}).filter(([, v]) => v !== null && v !== undefined)
    );

    const extractOneFile = async (file, idx) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await api.post("/purchases/extract", fd, { timeout: 180000 });
            const d = r.data;
            return {
                ok: true,
                idx,
                draft: {
                    kind: "vendor",
                    vendor: d.vendor || {},
                    bill_number: d.bill_number || "",
                    bill_date: d.bill_date || "",
                    line_items: (d.line_items || []).map((l) => ({ ...emptyLine, ...stripNulls(l) })),
                    subtotal: d.subtotal || 0, cgst: d.cgst || 0, sgst: d.sgst || 0, igst: d.igst || 0,
                    total_tax: d.total_tax || 0, grand_total: d.grand_total || 0,
                    file_name: d.file_name || file.name,
                    file_mime: d.file_mime || file.type,
                    file_data: d.file_data,
                    duplicate_of: d.duplicate_of,
                    confidence: d.confidence || {},
                },
            };
        } catch (e) {
            return { ok: false, idx, error: e.response?.data?.detail || e.message || "Extraction failed" };
        }
    };

    const uploadBulk = async (files) => {
        if (!files || files.length === 0) return;
        if (files.length > 20) return toast.error("Max 20 files at a time");
        const arr = Array.from(files);

        // Immediately show one placeholder row per file so the user sees progress
        const placeholders = arr.map((f, i) => ({
            _id: `${Date.now()}-${i}`,
            _status: "queued",       // queued | extracting | ready | failed
            _saved: false,
            _saving: false,
            kind: "vendor",
            file_name: f.name,
            file_mime: f.type,
            vendor: {},
            bill_number: "",
            bill_date: "",
            line_items: [],
            subtotal: 0, cgst: 0, sgst: 0, igst: 0, total_tax: 0, grand_total: 0,
            _file: f,               // keep original File for retries
        }));
        setBulkDrafts(placeholders);
        setBulkProgress({ total: arr.length, done: 0, failed: 0 });

        // Concurrency pool
        let next = 0;
        let done = 0;
        let failed = 0;

        const worker = async () => {
            while (true) {
                const myIdx = next++;
                if (myIdx >= arr.length) return;
                setBulkDrafts((prev) => prev.map((x, i) => i === myIdx ? { ...x, _status: "extracting" } : x));
                const res = await extractOneFile(arr[myIdx], myIdx);
                if (res.ok) {
                    setBulkDrafts((prev) => prev.map((x, i) => i === myIdx
                        ? { ...x, ...res.draft, _status: "ready", error: undefined }
                        : x));
                } else {
                    failed++;
                    setBulkDrafts((prev) => prev.map((x, i) => i === myIdx
                        ? { ...x, _status: "failed", error: res.error }
                        : x));
                }
                done++;
                setBulkProgress({ total: arr.length, done, failed });
            }
        };

        await Promise.all(Array.from({ length: Math.min(BULK_CONCURRENCY, arr.length) }, worker));

        const okCount = arr.length - failed;
        if (failed === 0) toast.success(`Parsed all ${okCount} bills. Review and save.`);
        else if (okCount === 0) toast.error(`All ${arr.length} bills failed to parse. You can retry each row.`);
        else toast.success(`Parsed ${okCount}/${arr.length} bills · ${failed} failed (retryable). Review and save.`);
        setBulkProgress(null);
    };

    const retryBulkOne = async (idx) => {
        const d = bulkDrafts[idx];
        if (!d || !d._file) return;
        setBulkDrafts((prev) => prev.map((x, i) => i === idx ? { ...x, _status: "extracting", error: undefined } : x));
        const res = await extractOneFile(d._file, idx);
        if (res.ok) {
            setBulkDrafts((prev) => prev.map((x, i) => i === idx
                ? { ...x, ...res.draft, _status: "ready", error: undefined }
                : x));
            toast.success(`${d.file_name}: parsed`);
        } else {
            setBulkDrafts((prev) => prev.map((x, i) => i === idx
                ? { ...x, _status: "failed", error: res.error }
                : x));
            toast.error(`${d.file_name}: ${res.error}`);
        }
    };

    const saveBulkOne = async (idx) => {
        const d = bulkDrafts[idx];
        if (!d || d._saved || d._status !== "ready") return;
        setBulkDrafts((prev) => prev.map((x, i) => i === idx ? { ...x, _saving: true, _saveError: undefined } : x));
        try {
            const { _id, _saved, _saving, _status, _file, _saveError, error, duplicate_of, confidence, ...body } = d;
            await api.post("/purchases", body);
            setBulkDrafts((prev) => prev.map((x, i) => i === idx ? { ...x, _saving: false, _saved: true, _saveError: undefined } : x));
        } catch (e) {
            const msg = e.response?.data?.detail
                        ? (typeof e.response.data.detail === "string"
                            ? e.response.data.detail
                            : JSON.stringify(e.response.data.detail))
                        : (e.message || "Save failed");
            toast.error(`${d.file_name}: ${msg}`);
            setBulkDrafts((prev) => prev.map((x, i) => i === idx ? { ...x, _saving: false, _saveError: msg } : x));
        }
    };

    const saveBulkAll = async () => {
        let saved = 0;
        for (let i = 0; i < bulkDrafts.length; i++) {
            const d = bulkDrafts[i];
            // Skip rows that are not ready, already saved, previously extraction-failed,
            // or previously save-failed (user must edit/retry them explicitly).
            if (!d._saved && !d.error && !d._saveError && d._status === "ready") {
                // eslint-disable-next-line no-await-in-loop
                await saveBulkOne(i);
                saved++;
            }
        }
        if (saved > 0) toast.success(`Batch saved · ${saved} bill${saved !== 1 ? "s" : ""}`);
        else toast.info("Nothing ready to save");
        load();
    };

    const updateBulk = (idx, patch) =>
        setBulkDrafts((prev) => prev.map((x, i) => i === idx ? { ...x, ...patch } : x));

    // --- Bulk print (merge attachments of selected purchases into one PDF) ---
    const bulkPrint = async () => {
        if (selected.size === 0) return toast.error("Select at least one bill");
        setPrinting(true);
        try {
            const r = await api.post("/purchases/bulk-print", { ids: Array.from(selected) },
                                    { responseType: "blob", timeout: 180000 });
            const url = window.URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `hdb-purchases-${selected.size}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
            const added = r.headers["x-hdb-added"];
            const skipped = r.headers["x-hdb-skipped"];
            toast.success(`Merged ${added} bill${added !== "1" ? "s" : ""} into one PDF${skipped && skipped !== "0" ? ` · ${skipped} skipped (no attachment)` : ""}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk print failed");
        }
        setPrinting(false);
    };

    return (
        <div data-testid="purchases-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Purchases</h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Vendor bills (auto-extracted from image/PDF) &amp; cash/imprest purchases. Data feeds the GST ITC report.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <label className="hdb-btn hdb-btn-primary cursor-pointer" data-testid="upload-vendor-bill-btn">
                        {uploading ? <><Loader2 size={13} className="animate-spin" /> Reading…</>
                                   : <><Upload size={13} /> Upload Vendor Bill</>}
                        <input type="file" className="hidden" disabled={uploading}
                               accept=".pdf,.jpg,.jpeg,.png,.webp,image/*,application/pdf"
                               onChange={(e) => e.target.files?.[0] && uploadVendorBill(e.target.files[0])}
                               data-testid="upload-vendor-bill-file" />
                    </label>
                    <label className="hdb-btn hdb-btn-ghost cursor-pointer" data-testid="bulk-upload-btn">
                        {bulkProgress ? <><Loader2 size={13} className="animate-spin" /> Parsing {bulkProgress.done}/{bulkProgress.total}…</>
                                     : <><Files size={13} /> Bulk Upload</>}
                        <input type="file" multiple className="hidden" disabled={!!bulkProgress}
                               accept=".pdf,.jpg,.jpeg,.png,.webp,image/*,application/pdf"
                               onChange={(e) => { uploadBulk(e.target.files); e.target.value = ""; }}
                               data-testid="bulk-upload-file" />
                    </label>
                    <button className="hdb-btn hdb-btn-mint" onClick={startCash} data-testid="log-cash-btn">
                        <HandCoins size={13} /> Log Cash / Imprest
                    </button>
                </div>
            </div>

            {/* Duplicate warning banner — only appears when duplicates detected */}
            {dupCount > 0 && !dupDismissed && (
                <div className="mb-5 rounded-xl border-2 border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
                     data-testid="dup-banner">
                    <AlertOctagon size={20} className="text-amber-600 shrink-0 mt-0.5"/>
                    <div className="flex-1 min-w-0">
                        <div className="font-black text-amber-900">
                            {dupCount} possible duplicate {dupCount === 1 ? "bill" : "bills"} detected
                        </div>
                        <div className="text-xs text-amber-800 mt-0.5">
                            Same vendor + bill number, or same vendor + date + total within ₹1.
                            These may double-count your GST input credit.
                        </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <button onClick={() => setDupModal(true)}
                                className="hdb-btn hdb-btn-primary"
                                data-testid="dup-review-btn">
                            Review &amp; remove
                        </button>
                        <button onClick={() => setDupDismissed(true)}
                                className="hdb-btn hdb-btn-ghost text-xs"
                                title="Hide banner until next visit"
                                data-testid="dup-dismiss-btn">
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {/* Filters + Totals */}
            <div className="hdb-card p-4 mb-5 flex flex-wrap gap-4 items-end">
                <div>
                    <label className="hdb-label">Kind</label>
                    <select className="hdb-input" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="purchase-kind-filter">
                        {KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
                    </select>
                </div>
                <div className="flex-1 min-w-[240px]">
                    <label className="hdb-label"><Search size={11} className="inline mr-1" /> Search</label>
                    <input className="hdb-input" placeholder="bill #, vendor name, staff name…" value={q}
                           onChange={(e) => setQ(e.target.value)} data-testid="purchase-search" />
                </div>
                <div className="ml-auto grid grid-cols-2 gap-6 pr-2">
                    <div className="text-right">
                        <div className="hdb-label">Total tax (ITC)</div>
                        <div className="hdb-num font-black text-xl text-amber-700">{fmt(totals.tax)}</div>
                    </div>
                    <div className="text-right">
                        <div className="hdb-label">Total spent</div>
                        <div className="hdb-num font-black text-xl text-slate-900">{fmt(totals.grand)}</div>
                    </div>
                </div>
            </div>

            <div className="hdb-card overflow-hidden">
                {selected.size > 0 && (
                    <div className="px-4 py-2 bg-slate-900 text-white flex items-center gap-3 flex-wrap" data-testid="bulk-toolbar">
                        <span className="text-sm font-semibold">{selected.size} selected</span>
                        <button className="hdb-btn hdb-btn-mint" onClick={bulkPrint} disabled={printing} data-testid="bulk-print-btn">
                            {printing ? <><Loader2 size={13} className="animate-spin" /> Merging…</>
                                     : <><Printer size={13} /> Print selected as one PDF</>}
                        </button>
                        <button className="hdb-btn hdb-btn-ghost" onClick={() => setSelected(new Set())}>Clear</button>
                    </div>
                )}
                <table className="hdb-table w-full">
                    <thead>
                        <tr>
                            <th style={{width: 32}}>
                                <button onClick={toggleSelAll} className="text-slate-400 hover:text-slate-900"
                                        data-testid="select-all-btn">
                                    {selected.size > 0 && selected.size === rows.filter(r => r.has_attachment).length
                                        ? <CheckSquare size={14} /> : <Square size={14} />}
                                </button>
                            </th>
                            <th>Bill #</th><th>Date</th><th>Vendor / Staff</th><th>Kind</th>
                            <th className="text-right">Subtotal</th><th className="text-right">Tax</th>
                            <th className="text-right">Grand</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} data-testid={`purchase-row-${r.id}`}
                                className={selected.has(r.id) ? "bg-emerald-50/50" : ""}>
                                <td>
                                    {r.has_attachment ? (
                                        <button onClick={() => toggleSel(r.id)}
                                                className="text-slate-400 hover:text-slate-900"
                                                data-testid={`select-${r.id}`}>
                                            {selected.has(r.id) ? <CheckSquare size={14} className="text-emerald-600" /> : <Square size={14} />}
                                        </button>
                                    ) : <span className="text-slate-300 text-[10px]" title="No attachment to print">—</span>}
                                </td>
                                <td className="hdb-mono font-semibold">{r.bill_number || <span className="text-slate-400">—</span>}</td>
                                <td className="hdb-mono">{r.bill_date}</td>
                                <td>
                                    <div className="font-medium text-slate-900">{r.kind === "cash_imprest" ? r.purchased_by : r.vendor?.name || "—"}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">{r.kind === "cash_imprest" ? (r.purpose || "General") : (r.vendor?.gstin || "")}</div>
                                </td>
                                <td>
                                    <span className={`hdb-tag ${r.kind === "cash_imprest" ? "hdb-tag-gold" : "hdb-tag-mint"}`}>
                                        {r.kind === "cash_imprest" ? "Cash" : "Vendor"}
                                    </span>
                                </td>
                                <td className="hdb-num hdb-num-right">{fmt(r.subtotal)}</td>
                                <td className="hdb-num hdb-num-right text-amber-700">{fmt(r.total_tax)}</td>
                                <td className="hdb-num hdb-num-right font-bold">{fmt(r.grand_total)}</td>
                                <td>
                                    <div className="flex gap-1 justify-end">
                                        {r.has_attachment && (
                                            <a href={`${API}/purchases/${r.id}/attachment`} target="_blank" rel="noreferrer"
                                               className="hdb-btn hdb-btn-ghost" title="View original bill"
                                               data-testid={`view-attachment-${r.id}`}>
                                                <ExternalLink size={12} />
                                            </a>
                                        )}
                                        <button className="hdb-btn hdb-btn-danger" onClick={() => remove(r)}
                                                data-testid={`delete-purchase-${r.id}`}>
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={9} className="text-center py-12 text-slate-400">
                                <Receipt size={22} className="mx-auto mb-2 opacity-60" />
                                No purchases yet. Upload a vendor bill or log a cash purchase.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Vendor-bill review drawer */}
            {draft && <VendorBillReview draft={draft} setDraft={setDraft} onSave={saveDraft} />}

            {/* Bulk-upload review list */}
            {bulkDrafts && (
                <BulkDraftsReview
                    drafts={bulkDrafts}
                    progress={bulkProgress}
                    onUpdate={updateBulk}
                    onSaveOne={saveBulkOne}
                    onSaveAll={saveBulkAll}
                    onRetry={retryBulkOne}
                    onClose={() => { setBulkDrafts(null); setBulkProgress(null); load(); }}
                />
            )}

            {/* Cash/imprest modal */}
            {cashOpen && (
                <CashImprestModal
                    draft={cashDraft} setDraft={setCashDraft}
                    onAttach={attachCashFile} onSave={saveCash}
                    onClose={() => { setCashOpen(false); setCashDraft(null); }}
                />
            )}

            {/* Duplicate review modal */}
            {dupModal && (
                <DuplicateReviewModal
                    onClose={() => { setDupModal(false); refreshDupCount(); }}
                    onChanged={() => { load(); refreshDupCount(); }}
                />
            )}
        </div>
    );
}


// ---------------------------------------------------------------------------
// Duplicate-bill scanner modal — groups of suspicious bills, staff picks
// which one to keep per group (radio) and clicks "Remove others".
// Removed rows get soft-deleted so they stay auditable.
// ---------------------------------------------------------------------------
function DuplicateReviewModal({ onClose, onChanged }) {
    const [groups, setGroups] = useState(null);
    const [keepId, setKeepId] = useState({});   // groupIdx → purchase_id to KEEP
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/purchases/duplicates");
            const list = r.data.groups || [];
            setGroups(list);
            // Default: keep the OLDEST (first uploaded) in each group
            const defaults = {};
            list.forEach((g, gi) => { defaults[gi] = g.members[0].id; });
            setKeepId(defaults);
        } catch (e) {
            toast.error("Couldn't scan for duplicates");
            setGroups([]);
        }
    };
    // eslint-disable-next-line
    useEffect(() => { load(); }, []);

    const removeOthers = async (gi) => {
        const g = groups[gi];
        const keep = keepId[gi];
        const toRemove = g.members.filter((m) => m.id !== keep);
        if (!toRemove.length) { toast.info("Nothing to remove"); return; }
        setBusy(true);
        try {
            await Promise.all(toRemove.map((m) =>
                api.post(`/purchases/${m.id}/mark-duplicate`, {
                    duplicate_of: keep, reason: g.rule,
                    note: `Auto-detected duplicate of ${g.members.find(x => x.id === keep)?.bill_number || keep}`,
                })
            ));
            toast.success(`Removed ${toRemove.length} duplicate${toRemove.length > 1 ? "s" : ""}`);
            await load();
            onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't mark as duplicate");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-3"
             onClick={onClose} data-testid="dup-modal">
            <div onClick={(e) => e.stopPropagation()}
                 className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl max-h-[88vh] flex flex-col">
                <header className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
                    <div>
                        <h3 className="text-xl font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                            Duplicate bill review
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                            Pick which bill to KEEP in each group — the rest will be soft-deleted
                            (kept in DB for audit, hidden from totals).
                        </p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-900"
                            data-testid="dup-modal-close"><X size={20}/></button>
                </header>

                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {groups === null && (
                        <div className="p-8 text-center text-slate-400">
                            <Loader2 size={16} className="inline animate-spin mr-2"/> Scanning…
                        </div>
                    )}
                    {groups && groups.length === 0 && (
                        <div className="p-8 text-center text-emerald-700 font-bold" data-testid="dup-all-clear">
                            <Check size={18} className="inline mr-2"/> No duplicates found.
                        </div>
                    )}
                    {groups && groups.map((g, gi) => (
                        <div key={gi} className="hdb-card border-2 border-amber-200 overflow-hidden"
                             data-testid={`dup-group-${gi}`}>
                            <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2 text-amber-900">
                                    <AlertTriangle size={13}/>
                                    <span className="text-xs font-bold">{g.reason}</span>
                                    <span className="hdb-tag" style={{background:"#FEE2E2",color:"#991B1B"}}>
                                        {g.members.length} bills
                                    </span>
                                </div>
                                <button onClick={() => removeOthers(gi)} disabled={busy}
                                        className="hdb-btn hdb-btn-primary text-xs"
                                        data-testid={`dup-remove-${gi}`}>
                                    {busy ? <><Loader2 size={11} className="animate-spin inline mr-1"/> Removing…</>
                                           : <><Check size={11}/> Keep selected, remove {g.members.length - 1}</>}
                                </button>
                            </div>
                            <table className="hdb-table w-full text-xs">
                                <thead><tr>
                                    <th className="w-8"></th>
                                    <th>Bill #</th><th>Date</th>
                                    <th className="text-right">Total</th>
                                    <th>Items</th><th>Uploaded</th><th>Attachment</th>
                                </tr></thead>
                                <tbody>
                                    {g.members.map((m) => (
                                        <tr key={m.id}
                                            className={keepId[gi] === m.id ? "bg-emerald-50" : "hover:bg-slate-50"}
                                            data-testid={`dup-member-${m.id}`}>
                                            <td className="text-center">
                                                <input type="radio" name={`keep-${gi}`}
                                                       checked={keepId[gi] === m.id}
                                                       onChange={() => setKeepId((s) => ({ ...s, [gi]: m.id }))}
                                                       data-testid={`dup-keep-${gi}-${m.id}`}/>
                                            </td>
                                            <td className="hdb-mono font-bold">{m.bill_number || "—"}</td>
                                            <td className="hdb-mono">{m.bill_date}</td>
                                            <td className="hdb-num hdb-num-right font-bold">{fmt(m.grand_total)}</td>
                                            <td className="text-slate-600">
                                                {m.item_count} item{m.item_count !== 1 ? "s" : ""}
                                                {m.item_preview && <span className="text-slate-400 ml-1">· {m.item_preview}</span>}
                                            </td>
                                            <td className="hdb-mono text-slate-500">
                                                {(m.created_at || "").slice(0, 10)}
                                            </td>
                                            <td>
                                                {m.has_attachment ? (
                                                    <a href={`${API}/purchases/${m.id}/attachment`} target="_blank" rel="noreferrer"
                                                       className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 font-bold"
                                                       data-testid={`dup-view-${m.id}`}>
                                                        <FileText size={11}/> View
                                                    </a>
                                                ) : (
                                                    <span className="text-slate-300">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>

                <footer className="px-6 py-3 border-t border-slate-200 text-[11px] text-slate-500 flex justify-between items-center">
                    <span>Removed bills are soft-deleted. Restore from Purchases → include removed.</span>
                    <button onClick={onClose} className="hdb-btn hdb-btn-ghost" data-testid="dup-done">Done</button>
                </footer>
            </div>
        </div>
    );
}


function VendorBillReview({ draft, setDraft, onSave }) {
    const setVendor = (k, v) => setDraft({ ...draft, vendor: { ...draft.vendor, [k]: v } });
    const setLine = (i, k, v) => {
        const li = [...draft.line_items];
        li[i] = { ...li[i], [k]: v };
        setDraft({ ...draft, line_items: li });
    };
    const rmLine = (i) => setDraft({ ...draft, line_items: draft.line_items.filter((_, x) => x !== i) });
    const addLine = () => setDraft({ ...draft, line_items: [...draft.line_items, { ...emptyLine }] });

    const conf = draft.confidence || {};
    const isLow = (k) => conf[k] === "low";

    return (
        <div className="fixed inset-0 z-50 flex bg-slate-900/40 backdrop-blur-sm overflow-y-auto"
             data-testid="bill-review-modal">
            <div className="hdb-card m-auto w-full max-w-5xl p-6 shadow-2xl my-8">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="hdb-heading font-bold text-lg text-slate-900 flex items-center gap-2">
                        <ShieldCheck size={18} /> Review vendor bill
                    </h3>
                    <button className="text-slate-400 hover:text-slate-600" onClick={() => setDraft(null)}>
                        <X size={18} />
                    </button>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                    AI-extracted. Cross-check every field, correct anything wrong, then save.
                    Fields flagged low-confidence are outlined amber.
                </p>

                {draft.duplicate_of && (
                    <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
                         data-testid="duplicate-warning">
                        <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                        <span>
                            Possible duplicate — this vendor+bill+total signature matches an existing
                            purchase (#{draft.duplicate_of.bill_number} · {draft.duplicate_of.bill_date}).
                            Continue only if this really is a separate purchase.
                        </span>
                    </div>
                )}

                {/* Vendor + bill meta */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="col-span-2">
                        <label className="hdb-label">Vendor name</label>
                        <input className="hdb-input" value={draft.vendor.name || ""}
                               onChange={(e) => setVendor("name", e.target.value)} data-testid="vendor-name-input" />
                    </div>
                    <div className={isLow("vendor_gstin") ? "ring-2 ring-amber-300 rounded-md" : ""}>
                        <label className="hdb-label">GSTIN {isLow("vendor_gstin") && <span className="text-amber-600 ml-1">?</span>}</label>
                        <input className="hdb-input hdb-mono" value={draft.vendor.gstin || ""}
                               onChange={(e) => setVendor("gstin", e.target.value.toUpperCase())} data-testid="vendor-gstin-input" />
                    </div>
                    <div>
                        <label className="hdb-label">State code</label>
                        <input className="hdb-input hdb-mono" value={draft.vendor.state_code || ""}
                               onChange={(e) => setVendor("state_code", e.target.value)} />
                    </div>
                    <div className={isLow("bill_number") ? "ring-2 ring-amber-300 rounded-md" : ""}>
                        <label className="hdb-label">Bill # {isLow("bill_number") && <span className="text-amber-600 ml-1">?</span>}</label>
                        <input className="hdb-input hdb-mono" value={draft.bill_number}
                               onChange={(e) => setDraft({ ...draft, bill_number: e.target.value })} data-testid="bill-number-input" />
                    </div>
                    <div className={isLow("bill_date") ? "ring-2 ring-amber-300 rounded-md" : ""}>
                        <label className="hdb-label">Bill date</label>
                        <input className="hdb-input hdb-mono" type="date" value={draft.bill_date}
                               onChange={(e) => setDraft({ ...draft, bill_date: e.target.value })} data-testid="bill-date-input" />
                    </div>
                    <div className="col-span-2">
                        <label className="hdb-label">Vendor address</label>
                        <input className="hdb-input" value={draft.vendor.address || ""}
                               onChange={(e) => setVendor("address", e.target.value)} />
                    </div>
                </div>

                {/* Line items */}
                <div className="hdb-card p-3 mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <div className="hdb-label">Line items · {draft.line_items.length}</div>
                        <button className="hdb-btn hdb-btn-ghost text-xs" onClick={addLine}>+ Add row</button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="hdb-table w-full min-w-[900px]">
                            <thead><tr>
                                <th>Item</th><th style={{ width: 80 }}>HSN</th>
                                <th style={{ width: 70 }}>Qty</th><th style={{ width: 70 }}>Unit</th>
                                <th style={{ width: 90 }} className="text-right">Rate</th>
                                <th style={{ width: 60 }} className="text-right">GST %</th>
                                <th style={{ width: 100 }} className="text-right">Line total</th>
                                <th></th>
                            </tr></thead>
                            <tbody>
                                {draft.line_items.map((li, i) => {
                                    const lc = li.confidence || {};
                                    return (
                                        <tr key={i}>
                                            <td><input className="hdb-input" value={li.name} onChange={(e) => setLine(i, "name", e.target.value)} data-testid={`bill-line-name-${i}`} /></td>
                                            <td><input className={`hdb-input hdb-mono ${lc.hsn === "low" ? "ring-2 ring-amber-300" : ""}`} value={li.hsn} onChange={(e) => setLine(i, "hsn", e.target.value)} /></td>
                                            <td><input className="hdb-input hdb-mono" type="number" value={li.qty} onChange={(e) => setLine(i, "qty", parseFloat(e.target.value || 0))} /></td>
                                            <td><input className="hdb-input" value={li.unit} onChange={(e) => setLine(i, "unit", e.target.value)} /></td>
                                            <td><input className={`hdb-input hdb-mono text-right ${lc.rate === "low" ? "ring-2 ring-amber-300" : ""}`} type="number" value={li.rate} onChange={(e) => setLine(i, "rate", parseFloat(e.target.value || 0))} /></td>
                                            <td><input className={`hdb-input hdb-mono text-right ${lc.tax_percent === "low" ? "ring-2 ring-amber-300" : ""}`} type="number" value={li.tax_percent} onChange={(e) => setLine(i, "tax_percent", parseFloat(e.target.value || 0))} /></td>
                                            <td><input className="hdb-input hdb-mono text-right" type="number" value={li.line_total} onChange={(e) => setLine(i, "line_total", parseFloat(e.target.value || 0))} /></td>
                                            <td><button className="hdb-btn hdb-btn-danger" onClick={() => rmLine(i)}><Trash2 size={12} /></button></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Totals */}
                <div className={`grid grid-cols-2 md:grid-cols-6 gap-3 mb-4 ${isLow("totals") ? "ring-2 ring-amber-300 rounded-md p-2" : ""}`}>
                    {[["subtotal","Subtotal"],["cgst","CGST"],["sgst","SGST"],["igst","IGST"],["total_tax","Total tax"],["grand_total","Grand total"]].map(([k, label]) => (
                        <div key={k}>
                            <label className="hdb-label">{label}</label>
                            <input className={`hdb-input hdb-mono text-right ${k === "grand_total" ? "font-bold" : ""}`}
                                   type="number" value={draft[k]}
                                   onChange={(e) => setDraft({ ...draft, [k]: parseFloat(e.target.value || 0) })}
                                   data-testid={`total-${k}`} />
                        </div>
                    ))}
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button className="hdb-btn hdb-btn-ghost" onClick={() => setDraft(null)}>Cancel</button>
                    <button className="hdb-btn hdb-btn-primary" onClick={onSave} data-testid="save-purchase-btn">
                        <ShieldCheck size={13} /> Save purchase
                    </button>
                </div>
            </div>
        </div>
    );
}


function BulkDraftsReview({ drafts, progress, onUpdate, onSaveOne, onSaveAll, onRetry, onClose }) {
    const readyCount   = drafts.filter((d) => d._status === "ready" && !d._saved && !d._saveError).length;
    const savedCount   = drafts.filter((d) => d._saved).length;
    const failedCount  = drafts.filter((d) => d._status === "failed" || d._saveError).length;
    const workingCount = drafts.filter((d) => d._status === "queued" || d._status === "extracting").length;
    const stillParsing = !!progress;

    return (
        <div className="fixed inset-0 z-50 flex bg-slate-900/40 backdrop-blur-sm overflow-y-auto"
             data-testid="bulk-drafts-modal">
            <div className="hdb-card m-auto w-full max-w-6xl p-6 shadow-2xl my-6">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <h3 className="hdb-heading font-bold text-lg text-slate-900 flex items-center gap-2">
                        <Files size={18} /> Bulk review · {drafts.length} bill{drafts.length !== 1 ? "s" : ""}
                        <span className="text-xs text-slate-500 font-normal ml-2" data-testid="bulk-summary">
                            {savedCount} saved · {readyCount} ready · {workingCount} working · {failedCount} failed
                        </span>
                    </h3>
                    <div className="flex gap-2">
                        <button className="hdb-btn hdb-btn-primary" onClick={onSaveAll} disabled={readyCount === 0}
                                data-testid="save-all-btn">
                            <ShieldCheck size={13} /> Save all ready ({readyCount})
                        </button>
                        <button className="hdb-btn hdb-btn-ghost" onClick={onClose} data-testid="bulk-close-btn">Close</button>
                    </div>
                </div>
                {stillParsing && (
                    <div className="mb-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-center gap-2"
                         data-testid="bulk-parsing-banner">
                        <Loader2 size={12} className="animate-spin" />
                        Extracting bills in the background · {progress.done}/{progress.total} done{progress.failed ? ` · ${progress.failed} failed` : ""}. You can already edit &amp; save ready rows below.
                    </div>
                )}
                <p className="text-xs text-slate-500 mb-4">
                    Rows appear as each bill finishes parsing. Failed rows can be retried without re-uploading everything.
                </p>

                <div className="space-y-3">
                    {drafts.map((d, idx) => {
                        const status = d._status || (d._saved ? "ready" : d.error ? "failed" : "ready");
                        const isWorking = status === "queued" || status === "extracting";
                        const isFailed  = status === "failed";
                        return (
                        <div key={d._id}
                             className={`hdb-card p-4 ${d._saved ? "opacity-60 bg-emerald-50/30" : isFailed ? "bg-red-50/40 border-red-300" : isWorking ? "bg-slate-50/60 border-slate-200" : ""}`}
                             data-testid={`bulk-row-${idx}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex-1 min-w-[200px]">
                                    <div className="text-xs text-slate-500 hdb-mono">{d.file_name}</div>
                                    {isWorking ? (
                                        <div className="text-slate-600 text-sm mt-1 flex items-center gap-1" data-testid={`bulk-row-${idx}-status`}>
                                            {status === "queued"
                                                ? <><Clock size={12} /> Queued…</>
                                                : <><Loader2 size={12} className="animate-spin" /> Extracting with AI…</>}
                                        </div>
                                    ) : isFailed ? (
                                        <div className="text-red-700 text-sm mt-1 flex items-center gap-1" data-testid={`bulk-row-${idx}-error`}>
                                            <AlertTriangle size={12} /> {d.error || "Extraction failed"}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-slate-900 font-semibold mt-0.5">
                                            {d.vendor?.name || "(unknown vendor)"}
                                            {d.duplicate_of && !d._saved && (
                                                <span className="hdb-tag hdb-tag-gold ml-2 text-[10px]">
                                                    <AlertTriangle size={9} className="inline mr-0.5" />
                                                    duplicate?
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {isFailed && (
                                        <button className="hdb-btn hdb-btn-ghost text-xs" onClick={() => onRetry(idx)}
                                                data-testid={`retry-bulk-${idx}`}>
                                            <RotateCw size={11} /> Retry
                                        </button>
                                    )}
                                    {!isFailed && !isWorking && (d._saved ? (
                                        <span className="hdb-tag hdb-tag-mint">
                                            <ShieldCheck size={10} className="inline mr-1" /> Saved
                                        </span>
                                    ) : (
                                        <button className="hdb-btn hdb-btn-primary text-xs"
                                                disabled={d._saving}
                                                onClick={() => onSaveOne(idx)}
                                                data-testid={`save-bulk-${idx}`}>
                                            {d._saving ? <><Loader2 size={11} className="animate-spin" /> Saving…</>
                                                      : <><ShieldCheck size={11} /> Save</>}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {!isFailed && !isWorking && (
                                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-3">
                                    <div className="md:col-span-2">
                                        <label className="hdb-label">Vendor</label>
                                        <input className="hdb-input" value={d.vendor?.name || ""} disabled={d._saved}
                                               onChange={(e) => onUpdate(idx, { vendor: { ...(d.vendor||{}), name: e.target.value } })} />
                                    </div>
                                    <div>
                                        <label className="hdb-label">GSTIN</label>
                                        <input className="hdb-input hdb-mono" value={d.vendor?.gstin || ""} disabled={d._saved}
                                               onChange={(e) => onUpdate(idx, { vendor: { ...(d.vendor||{}), gstin: e.target.value.toUpperCase() } })} />
                                    </div>
                                    <div>
                                        <label className="hdb-label">Bill #</label>
                                        <input className="hdb-input hdb-mono" value={d.bill_number} disabled={d._saved}
                                               onChange={(e) => onUpdate(idx, { bill_number: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="hdb-label">Date</label>
                                        <input className="hdb-input hdb-mono" type="date" value={d.bill_date} disabled={d._saved}
                                               onChange={(e) => onUpdate(idx, { bill_date: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="hdb-label">Grand total</label>
                                        <input className="hdb-input hdb-mono text-right font-bold" type="number" value={d.grand_total} disabled={d._saved}
                                               onChange={(e) => onUpdate(idx, { grand_total: parseFloat(e.target.value || 0) })} />
                                    </div>
                                </div>
                            )}
                            {!isFailed && !isWorking && (
                                <div className="text-[11px] text-slate-500 mt-2">
                                    {d.line_items?.length || 0} line item{d.line_items?.length !== 1 ? "s" : ""} · tax ₹{fmtNum(d.total_tax || 0)} · subtotal ₹{fmtNum(d.subtotal || 0)}
                                </div>
                            )}
                            {d._saveError && !d._saved && (
                                <div className="mt-2 p-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-1"
                                     data-testid={`bulk-row-${idx}-save-error`}>
                                    <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                                    <span><b>Save failed:</b> {d._saveError} — edit the fields above then click Save again.</span>
                                </div>
                            )}
                        </div>
                    );})}
                </div>
            </div>
        </div>
    );
}


function CashImprestModal({ draft, setDraft, onAttach, onSave, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
             data-testid="cash-modal">
            <div className="hdb-card w-full max-w-lg p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="hdb-heading font-bold text-lg text-slate-900 flex items-center gap-2">
                        <HandCoins size={18} /> Cash / Imprest purchase
                    </h3>
                    <button className="text-slate-400 hover:text-slate-600" onClick={onClose}><X size={18} /></button>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                    Handwritten or cash bill. Attach a photo of the bill for records. Full HSN/GST breakup isn't needed.
                </p>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="hdb-label">Purchased by (staff) *</label>
                        <input className="hdb-input" value={draft.purchased_by}
                               onChange={(e) => setDraft({ ...draft, purchased_by: e.target.value })}
                               data-testid="cash-staff-name" />
                    </div>
                    <div>
                        <label className="hdb-label">Purpose / client (optional)</label>
                        <input className="hdb-input" value={draft.purpose}
                               onChange={(e) => setDraft({ ...draft, purpose: e.target.value })}
                               placeholder="e.g. Naluni School delivery" />
                    </div>
                    <div className="col-span-2">
                        <label className="hdb-label">Vendor / shop name</label>
                        <input className="hdb-input" value={draft.vendor.name}
                               onChange={(e) => setDraft({ ...draft, vendor: { ...draft.vendor, name: e.target.value } })} />
                    </div>
                    <div>
                        <label className="hdb-label">Bill date</label>
                        <input className="hdb-input hdb-mono" type="date" value={draft.bill_date}
                               onChange={(e) => setDraft({ ...draft, bill_date: e.target.value })} />
                    </div>
                    <div>
                        <label className="hdb-label">Amount *</label>
                        <input className="hdb-input hdb-mono text-right" type="number" value={draft.grand_total}
                               onChange={(e) => setDraft({ ...draft, grand_total: parseFloat(e.target.value || 0) })}
                               data-testid="cash-amount" />
                    </div>
                    <div className="col-span-2">
                        <label className="hdb-label">Notes</label>
                        <input className="hdb-input" value={draft.notes}
                               onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                        <label className="hdb-label">Bill photo (optional)</label>
                        <input type="file" accept="image/*,application/pdf"
                               onChange={(e) => onAttach(e.target.files?.[0])}
                               className="text-sm" data-testid="cash-file" />
                        {draft.file_name && <div className="text-xs text-emerald-700 mt-1">Attached · {draft.file_name}</div>}
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-4 mt-3 border-t border-slate-100">
                    <button className="hdb-btn hdb-btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="hdb-btn hdb-btn-primary" onClick={onSave} data-testid="save-cash-btn">
                        <ShieldCheck size={13} /> Save cash purchase
                    </button>
                </div>
            </div>
        </div>
    );
}
